import { Loader2, MoreHorizontal, UserPlus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Card, Field, btnGhost, btnPrimary, inputBaseCls, inputCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';
import { SiteUsers } from './site-users';

type Role = 'admin' | 'editor';
interface PanelUser {
  username: string;
  email: string;
  name?: string;
  role: Role | null;
  status: string;
  enabled: boolean;
  mfa: boolean;
  createdAt?: string;
}
type Action = 'disable' | 'enable' | 'resend' | 'reset-password' | 'reset-mfa' | 'delete';

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin (tutto, anche utenti)',
  editor: 'Redazione (eventi e contenuti)',
};

const errorText = (e: unknown) =>
  e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);

function state(u: PanelUser): [string, string] {
  if (!u.enabled) return ['Disattivato', 'bg-surface-strong text-muted'];
  if (u.status === 'FORCE_CHANGE_PASSWORD') return ['Invito in attesa', 'bg-accent-soft'];
  if (u.status === 'RESET_REQUIRED') return ['Password da reimpostare', 'bg-accent-soft'];
  return ['Attivo', 'bg-ok-soft'];
}

/**
 * Utenti (solo admin, docs/07-admin-e-bot.md "Utenti"): due schede, chi entra nel pannello
 * (`PanelUsers`) e chi entra sul sito con email e codice (`SiteUsers`, `?tab=site`).
 */
export function Users() {
  const { isAdmin } = useSession();
  const [params] = useSearchParams();
  const site = params.get('tab') === 'site';
  if (!isAdmin)
    return <main className="p-6 text-muted">La gestione degli utenti è riservata agli admin.</main>;
  const tab = (active: boolean) =>
    `px-4 py-2 text-sm ${active ? 'border-b-2 border-fg font-medium' : 'text-muted hover:text-fg'}`;
  return (
    <main className="mx-auto max-w-[1100px] p-4 md:p-6">
      <h1 className="font-display text-h2">Utenti</h1>
      <nav
        className="mt-3 mb-4 flex gap-2 border-b border-surface-strong"
        aria-label="Tipo di utenti"
      >
        <Link to="/users" className={tab(!site)} aria-current={!site ? 'page' : undefined}>
          Pannello
        </Link>
        <Link to="/users?tab=site" className={tab(site)} aria-current={site ? 'page' : undefined}>
          Sito
        </Link>
      </nav>
      {site ? <SiteUsers /> : <PanelUsers />}
    </main>
  );
}

/**
 * Utenti del pannello: inviti con il ruolo, cambio di ruolo, disattivazione, nuovo invito,
 * password e secondo fattore, eliminazione.
 */
function PanelUsers() {
  const { api, isAdmin } = useSession();
  const [users, setUsers] = useState<PanelUser[] | null>(null);
  const [me, setMe] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('editor');

  const load = async () => {
    try {
      const r = await api<{ users: PanelUser[]; me: string }>('/users');
      setUsers(r.users);
      setMe(r.me);
    } catch (e) {
      setError(errorText(e));
    }
  };
  useEffect(() => {
    if (isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const run = async (key: string, fn: () => Promise<string>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      setNotice(await fn());
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const invite = (ev: FormEvent) => {
    ev.preventDefault();
    void run('invite', async () => {
      await api('/users', {
        method: 'POST',
        body: { email, role, ...(name.trim() ? { name: name.trim() } : {}) },
      });
      const sent = email;
      setEmail('');
      setName('');
      return `Invito mandato a ${sent}: riceve una password temporanea valida 7 giorni.`;
    });
  };

  const changeRole = (u: PanelUser, next: Role) =>
    run(u.username, async () => {
      await api(`/users/${u.username}`, { method: 'PATCH', body: { role: next } });
      return `${u.email} ora è ${next === 'admin' ? 'admin' : 'della redazione'}: vale dal prossimo accesso.`;
    });

  const act = (u: PanelUser, action: Action) => {
    const confirmText: Partial<Record<Action, string>> = {
      disable: `Disattivare ${u.email}? Non potrà più entrare finché non lo riattivi.`,
      delete: `Eliminare ${u.email}? Non si può annullare: per farlo rientrare servirà un nuovo invito.`,
      'reset-mfa': `Togliere l’autenticazione a due fattori a ${u.email}? Potrà riattivarla da “Il mio account”.`,
      'reset-password': `Mandare a ${u.email} un codice per scegliere una password nuova? Quella attuale non varrà più.`,
    };
    if (confirmText[action] && !window.confirm(confirmText[action])) return;
    const done: Record<Action, string> = {
      disable: `${u.email} disattivato.`,
      enable: `${u.email} riattivato.`,
      resend: `Nuovo invito mandato a ${u.email}.`,
      'reset-password': `${u.email} riceve un codice per la password nuova.`,
      'reset-mfa': `Autenticazione a due fattori tolta a ${u.email}.`,
      delete: `${u.email} eliminato.`,
    };
    void run(u.username, async () => {
      if (action === 'delete') await api(`/users/${u.username}`, { method: 'DELETE' });
      else await api(`/users/${u.username}/${action}`, { method: 'POST' });
      return done[action];
    });
  };

  return (
    <div>
      <p className="max-w-[70ch] text-sm text-muted">
        Chi entra nel pannello. Si entra solo su invito: l’email arriva con una password temporanea
        da cambiare al primo accesso. Resta sempre almeno un admin attivo e nessuno può togliere
        l’accesso a sé stesso.
      </p>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-4 bg-ok-soft p-3 text-sm">
          {notice}
        </p>
      )}

      <div className="mt-4 flex flex-col gap-6">
        <Card title="Invita una persona">
          <form onSubmit={invite} className="flex flex-wrap items-end gap-3">
            <Field label="Email" className="min-w-[240px] flex-1">
              <input
                type="email"
                required
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label="Nome (facoltativo)" className="w-[180px]">
              <input
                className={inputCls}
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Ruolo" className="w-[240px]">
              <select
                className={inputCls}
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {(Object.keys(ROLE_LABELS) as Role[]).map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </Field>
            <button type="submit" className={btnPrimary} disabled={busy === 'invite'}>
              {busy === 'invite' ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <UserPlus className="size-4" aria-hidden />
              )}
              Invita
            </button>
          </form>
        </Card>

        <Card title={`Utenti${users ? ` (${users.length})` : ''}`}>
          {!users ? (
            <p className="text-sm text-muted">Caricamento…</p>
          ) : (
            <ul className="flex flex-col divide-y divide-surface-strong">
              {users.map((u) => {
                const [label, cls] = state(u);
                const self = u.username === me;
                return (
                  <li key={u.username} className="flex flex-wrap items-center gap-3 py-3 text-sm">
                    <span className="min-w-[220px] flex-1">
                      <b>{u.name ? `${u.name} · ` : ''}</b>
                      {u.email}
                      {self && <span className="text-muted"> (tu)</span>}
                      <span className="block text-xs text-muted">
                        {u.mfa ? 'Due fattori attivi' : 'Senza due fattori'}
                        {u.createdAt ? ` · invitato ${formatStamp(u.createdAt)}` : ''}
                      </span>
                    </span>
                    <span className={`px-2 py-0.5 text-xs ${cls}`}>{label}</span>
                    <select
                      aria-label={`Ruolo di ${u.email}`}
                      className={`${inputBaseCls} w-[150px]`}
                      value={u.role ?? ''}
                      disabled={self || busy === u.username}
                      onChange={(e) => void changeRole(u, e.target.value as Role)}
                    >
                      {!u.role && <option value="">Nessun ruolo</option>}
                      <option value="admin">Admin</option>
                      <option value="editor">Redazione</option>
                    </select>
                    <details className="relative">
                      <summary
                        className={`${btnGhost} cursor-pointer list-none`}
                        aria-label={`Altre azioni per ${u.email}`}
                      >
                        {busy === u.username ? (
                          <Loader2 className="size-4 animate-spin" aria-hidden />
                        ) : (
                          <MoreHorizontal className="size-4" aria-hidden />
                        )}
                      </summary>
                      <div className="absolute right-0 z-10 mt-1 flex w-60 flex-col border border-surface-strong bg-page py-1 shadow-lg">
                        {u.status === 'FORCE_CHANGE_PASSWORD' ? (
                          <MenuItem onClick={() => act(u, 'resend')}>
                            Manda di nuovo l’invito
                          </MenuItem>
                        ) : (
                          <MenuItem onClick={() => act(u, 'reset-password')}>
                            Reimposta la password
                          </MenuItem>
                        )}
                        {u.mfa && (
                          <MenuItem onClick={() => act(u, 'reset-mfa')}>
                            Togli i due fattori (telefono perso)
                          </MenuItem>
                        )}
                        {!self &&
                          (u.enabled ? (
                            <MenuItem onClick={() => act(u, 'disable')}>Disattiva</MenuItem>
                          ) : (
                            <MenuItem onClick={() => act(u, 'enable')}>Riattiva</MenuItem>
                          ))}
                        {!self && (
                          <MenuItem danger onClick={() => act(u, 'delete')}>
                            Elimina
                          </MenuItem>
                        )}
                      </div>
                    </details>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function MenuItem({
  onClick,
  danger,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open');
        onClick();
      }}
      className={`px-3 py-2 text-left text-sm hover:bg-surface ${danger ? 'text-danger' : ''}`}
    >
      {children}
    </button>
  );
}
